import React, { useState, useEffect } from 'react';
import { Facebook, Instagram, MessageCircle, Phone, Mail, MapPin, Menu, X, Sun, Moon } from 'lucide-react';
import { supabase } from './lib/supabase';
import { BookingForm } from './components/BookingForm';
import { AuthModal } from './components/AuthModal';
import { useImageOptimization } from './hooks/useImageOptimization';
import { logger } from './lib/logger';
import { SEO } from './components/SEO';
import { ErrorBoundary } from './components/ErrorBoundary';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [user, setUser] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark';
    }
    return false;
  });
  const [hasScrolled, setHasScrolled] = useState(false);
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    try {
      supabase.auth.getSession().then(({ data: { session } }) => {
        setUser(session?.user ?? null);
      });

      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        setUser(session?.user ?? null);
      });

      return () => subscription.unsubscribe();
    } catch (error) {
      logger.error('Auth error:', error);
    }
  }, []);

  useEffect(() => {
    try {
      document.documentElement.classList.toggle('dark', isDarkMode);
      localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    } catch (error) {
      logger.error('Theme toggle error:', error);
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <ErrorBoundary>
      <SEO />
      <div className={`min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-300`}>
        {/* Fixed header container */}
        <div className={`fixed w-full z-50 transition-all duration-300`}>
          {/* Promotional Banner */}
          {showBanner && (
            <div className="promo-banner relative" style={{ backgroundColor: '#fec6df', color: '#000000' }}>
              <a 
                href="https://api.whatsapp.com/send/?phone=243984836331&text&type=phone_number&app_absent=22"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <div className="container mx-auto">
                  <div className="promo-banner__content">
                    <img 
                      src="https://www.notion.com/_next/image?url=https%3A%2F%2Fimages.ctfassets.net%2Fspoqsaf9291f%2F6RK2iMCvAJGrYom591owmp%2F1b3a5946b918b9e1cd03cda5284b18c2%2Fmap.png&w=640&q=75"
                      alt="Staff"
                      className="h-10 w-auto"
                    />
                    <span className="promo-banner__desktop">
                      Découvrez Karibu ! Un nouveau programme d'hospitalité conçu pour les voyageurs curieux qui vous permet d'obtenir des avantages plus importants. Contactez-nous pour en savoir plus. Jambo !
                    </span>
                    <span className="promo-banner__mobile">
                      Karibu - nouveau programme d'hospitalité pour les voyageurs currieux.
                    </span>
                  </div>
                </div>
              </a>
              <button 
                className="promo-banner__close"
                onClick={() => setShowBanner(false)}
                aria-label="Fermer la bannière"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className={`w-full transition-all duration-300 ${
            hasScrolled 
              ? 'bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm shadow-sm' 
              : 'bg-transparent'
          }`}>
            <div className="container mx-auto px-6">
              <div className="flex items-center justify-between h-20">
                {/* Logo avec transition de couleur */}
                <a href="/" className="flex items-center">
                  <img 
                    src={isDarkMode 
                      ? "https://media.canva.com/v2/image-resize/format:PNG/height:550/quality:100/uri:s3%2F%2Fmedia-private.canva.com%2FzUtRE%2FMAFkrXzUtRE%2F1%2Fp.png/watermark:F/width:532"
                      : "https://media.canva.com/v2/image-resize/format:PNG/height:550/quality:100/uri:s3%2F%2Fmedia-private.canva.com%2F0lKYE%2FMAFkrV0lKYE%2F1%2Fp.png/watermark:F/width:532"
                    }
                    alt="Jadoby Hotel Logo"
                    className="h-16 w-auto transition-opacity duration-300"
                  />
                </a>

                {/* Actions */}
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="btn-reserver px-6 py-2 bg-[#ef2543] text-white rounded-full hover:bg-[#ef2543]/80 transition-colors"
                  >
                    Réserver
                  </button>
                  <button
                    onClick={toggleTheme}
                    className={`p-2 rounded-full transition-colors ${
                      hasScrolled
                        ? 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                        : 'text-white hover:bg-white/10'
                    }`}
                    aria-label={isDarkMode ? "Activer le mode clair" : "Activer le mode sombre"}
                    title={isDarkMode ? "Passer en mode clair" : "Passer en mode sombre"}
                  >
                    {isDarkMode ? (
                      <Sun className="w-5 h-5" />
                    ) : (
                      <Moon className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </nav>
        </div>

        {/* Hero Section with adjusted margin-top */}
        <div className="relative h-screen">
          <img
            src="https://images.unsplash.com/photo-1618773928121-c32242e63f39?auto=format&fit=crop&q=80"
            alt="Jadoby Hôtel"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-50" />
          <div className="absolute inset-0 flex items-center justify-center text-center">
            <div className="container mx-auto px-6">
              <h2 className="hero-title text-4xl md:text-6xl font-bold text-white mb-4">
                Bienvenue à Jadoby Hôtel
              </h2>
              <p className="text-xl md:text-2xl text-white mb-8">
                Le coin des voyageurs
              </p>
              <button
                onClick={() => setShowBookingModal(true)}
                className="btn-hero px-8 py-3 bg-[#ef2543] text-white rounded-full text-lg hover:bg-[#ef2543]/80 transition-colors"
              >
                Réserver maintenant
              </button>
            </div>
          </div>
        </div>

        {/* Section slogan */}
        <div className="py-12 bg-[#0a0a0a] text-white">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold">
              Les petits prix pour les grands voyageurs
            </h2>
          </div>
        </div>

        {/* Section de réservation avec iFrame */}
        <div className="relative py-12 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&q=80')] bg-fixed bg-center bg-no-repeat bg-cover opacity-5"></div>
          <div className="container mx-auto px-4 relative">
            <div className="max-w-6xl mx-auto backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 rounded-2xl shadow-2xl">
              <div className="p-6 md:p-8">
                <div className="max-w-3xl mx-auto text-center mb-6">
                  <h3 className="text-2xl md:text-3xl font-bold mb-4 bg-gradient-to-r from-[#ef2543] to-[#ff6b6b] bg-clip-text text-transparent">
                    Réservez votre séjour
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Profitez de nos meilleures offres en réservant directement en ligne
                  </p>
                </div>
                <div className="relative aspect-[16/9] w-full overflow-hidden rounded-xl bg-white dark:bg-gray-900 shadow-inner">
                  <iframe
                    src="https://jadoby-hotel.amenitiz.io/fr/booking/room"
                    className="absolute inset-0 w-full h-full transition-transform duration-300 hover:scale-[1.001]"
                    title="Système de réservation"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section Chambres */}
        <section className="py-20 bg-white dark:bg-gray-900">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Nos Chambres</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Chambre Standard */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden shadow-lg transition-transform hover:scale-[1.02]">
                <img
                  src="https://fastly.4sqi.net/img/general/width960/512534229_driqZFc3pUZoz5YtVAeAeOh1ZC38qQViNBXPT_l0jgk.jpg"
                  alt="Chambre Standard"
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">Chambre Élégance</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Que vous soyez un voyageurs solo ou un professionnel en déplacement, profitez d'un espace confortable et minimaliste, spécialement conçu pour les aventuriers indépendants en quête d'une expérience unique et d'une escapade parfaite à petit budget.
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-[#ef2543] font-bold">À partir de 45 $ / nuit (petit-déjeuner inclus)</span>
                    <button
                      onClick={() => setShowBookingModal(true)}
                      className="px-4 py-2 bg-[#ef2543] text-white rounded-full hover:bg-[#ef2543]/80 transition-colors"
                    >
                      Réserver
                    </button>
                  </div>
                </div>
              </div>

              {/* Chambre Deluxe */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden shadow-lg transition-transform hover:scale-[1.02]">
                <img
                  src="https://scontent.ffih1-2.fna.fbcdn.net/v/t1.6435-9/37393463_694440937560092_4137907282652430336_n.jpg?_nc_cat=109&ccb=1-7&_nc_sid=127cfc&_nc_eui2=AeGWL9M-TkCYHR5IkCo3lQRaAgSBw58IZCkCBIHDnwhkKVrvd1vfbRd6df7xSlzV7hxRSq7Y_vaxbE1xWyzfllaN&_nc_ohc=DHlYY1IRBRIQ7kNvgGzuITV&_nc_oc=AdgIYJpIoRs44KwUItNUhyIDC1LAbaziQog84OP9W1t89WMM6LJiVc9WVSNbXwwtDvA&_nc_zt=23&_nc_ht=scontent.ffih1-2.fna&_nc_gid=Aa7jgV9Gcjek2C7mLnAu32V&oh=00_AYCLUPjDK-Ktr7YcEDI1Z6b47OCFWgzRmcWTeT6hCobJGw&oe=67D3FF76"
                  alt="Chambre Deluxe"
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">Chambre Deluxe</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                   Offrez-vous une escapade romantique dans une chambre conçue pour ceux qui aspirent à un séjour mémorable en duo. Profitez d'un cadre luxueux alliant élégance et commodités modernes, créant l'ambiance parfaite pour votre retraite amoureuse. 
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-[#ef2543] font-bold">À partir de 65 $ / nuit (petit-déjeuner inclus)</span>
                    <button
                      onClick={() => setShowBookingModal(true)}
                      className="px-4 py-2 bg-[#ef2543] text-white rounded-full hover:bg-[#ef2543]/80 transition-colors"
                    >
                      Réserver
                    </button>
                  </div>
                </div>
              </div>

              {/* Chambre Prestige */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden shadow-lg transition-transform hover:scale-[1.02]">
                <img
                  src="https://scontent.ffih1-2.fna.fbcdn.net/v/t1.6435-9/71753319_993697540967762_4136222886673252352_n.jpg?_nc_cat=101&ccb=1-7&_nc_sid=3a1ebe&_nc_eui2=AeGVdVXGpWID0mZsDi51yMYBKNundxxFVaMo26d3HEVVo1n-BKxEmPb_txWl3jV1ZFOzGb8pSWk3UrhKq9iaMl-3&_nc_ohc=HBgF5k5IX5AQ7kNvgEOVXzE&_nc_oc=AdgM-i5KYzrUBRSVn3n5qvOMRf9GHxG0nKIX8XsLCDscabKI1eHjbRkZsTTH5QzJqow&_nc_zt=23&_nc_ht=scontent.ffih1-2.fna&_nc_gid=AOqX6iQbEPZPaBVxAR3t1_A&oh=00_AYCeLQL99kOI4SEuiQ9YJbszDtpsXnTZ_b6R-LWX8aXM_Q&oe=67D401C2"
                  alt="Suite"
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">Chambre Prestige</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Idéale pour les longs séjours, cette chambre offre aux voyageurs exigeants un espace généreux et un confort supérieur. Profitez d'un cadre luxueux alliant élégance et commodités modernes, conçu pour répondre à toutes vos attentes et garantir un séjour inoubliable.
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-[#ef2543] font-bold">À partir de 80 $ / nuit (petit-déjeuner inclus)</span>
                    <button
                      onClick={() => setShowBookingModal(true)}
                      className="px-4 py-2 bg-[#ef2543] text-white rounded-full hover:bg-[#ef2543]/80 transition-colors"
                    >
                      Réserver
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section Services */}
        <section className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="container mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Nos Services</h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Découvrez notre gamme complète de services haut de gamme conçus pour rendre votre séjour à Kinshasa inoubliable
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Restaurant */}
              <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-[#ef2543] rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold ml-4">Restaurant</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300">
                  Savourez une expérience culinaire unique avec notre cuisine raffinée mêlant saveurs locales et internationales.
                </p>
              </div>

              {/* Service en chambre */}
              <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-[#ef2543] rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold ml-4">Service en Chambre</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300">
                  Profitez d'un service en chambre avec un menu varié et des options de restauration personnalisées pour votre confort absolu.
                </p>
              </div>

              {/* Wifi */}
              <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-[#ef2543] rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold ml-4">Internet Haut Débit</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300">
                  Restez connecté avec notre WiFi gratuit haute vitesse disponible dans tout l'hôtel. Parfait pour le travail ou le divertissement.
                </p>
              </div>

              {/* Parking */}
              <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-[#ef2543] rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold ml-4">Parking Privé Sécurisé</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300">
                  Garez votre véhicule en toute tranquillité dans notre parking surveillé 24h/24 avec service voiturier disponible sur demande.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-white dark:bg-gray-900">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Contactez-nous
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <MapPin className="w-6 h-6 text-[#ef2543]" />
                    <span>121, avenue Kalembelembe, Kinshasa, Congo</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Phone className="w-6 h-6 text-[#ef2543]" />
                    <a href="tel:+243990000468" className="hover:text-[#ef2543]">
                      +243 984 836 331
                    </a>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Mail className="w-6 h-6 text-[#ef2543]" />
                    <a href="mailto:jadobyhotel.info@gmail.com" className="hover:text-[#ef2543]">
                      jadobyhotel.info@gmail.com
                    </a>
                  </div>
                  <div className="flex space-x-4">
                    <a href="https://facebook.com/jadobyhotel" className="text-[#ef2543] hover:text-[#ef2543]/80">
                      <Facebook className="w-6 h-6" />
                    </a>
                    <a href="https://www.instagram.com/jadobyhotel" className="text-[#ef2543] hover:text-[#ef2543]/80">
                      <Instagram className="w-6 h-6" />
                    </a>
                  </div>
                </div>
                <form className="space-y-4">
                  <input
                    type="text"
                    placeholder="Votre nom"
                    className="w-full px-4 py-2 rounded-md border border-gray-300 focus:border-[#ef2543] focus:ring focus:ring-[#ef2543] focus:ring-opacity-50"
                  />
                  <input
                    type="email"
                    placeholder="Votre email"
                    className="w-full px-4 py-2 rounded-md border border-gray-300 focus:border-[#ef2543] focus:ring focus:ring-[#ef2543] focus:ring-opacity-50"
                  />
                  <textarea
                    placeholder="Votre message"
                    rows={4}
                    className="w-full px-4 py-2 rounded-md border border-gray-300 focus:border-[#ef2543] focus:ring focus:ring-[#ef2543] focus:ring-opacity-50"
                  />
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-[#ef2543] text-white rounded-md hover:bg-[#ef2543]/80 transition-colors"
                  >
                    Envoyer
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-6 bg-gray-100 dark:bg-gray-800 border-t dark:border-gray-700">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div className="text-center md:text-left">
                <small className="text-gray-600 dark:text-gray-400">
                  Copyright © {new Date().getFullYear()} Jadoby Hôtel. All Rights Reserved.
                </small>
              </div>
              <div className="text-center md:text-right">
                <small className="text-gray-600 dark:text-gray-400">
                  Developed by{' '}
                  <a 
                    href="https://avovatechno.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#ef2543] hover:text-[#ef2543]/80 transition-colors"
                  >
                    Avova Techno
                  </a>
                </small>
              </div>
            </div>
          </div>
        </footer>

        {showBookingModal && (
          <BookingForm onSuccess={() => setShowBookingModal(false)} />
        )}

        {showAuthModal && (
          <AuthModal onClose={() => setShowAuthModal(false)} />
        )}
      </div>
    </ErrorBoundary>
  );
}

export default App;